import React, { useState } from "react";
import { Link } from "react-router-dom";
import { formatCurrency, formatPercent } from "@/utils/formatters";
import LaggardAnalysisModal from "./modals/LaggardAnalysisModal";

const LaggardWidget = ({ accountId, laggards, loading }) => {
  const [showModal, setShowModal] = useState(false);

  // Get top 3 laggards (most negative momentum scores)
  const topLaggards = laggards
    ? [...laggards]
        .sort((a, b) => a.momentum_score - b.momentum_score)
        .slice(0, 3)
    : [];

  const getMomentumColor = (score) => {
    if (score <= -100) return "text-red-600 dark:text-red-400";
    if (score <= -50) return "text-orange-600 dark:text-orange-400";
    return "text-yellow-600 dark:text-yellow-400";
  };

  const getMomentumLabel = (score) => {
    if (score <= -200) return "Critical";
    if (score <= -100) return "Severe";
    if (score <= -50) return "Moderate";
    return "Mild";
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Momentum Laggards
        </h3>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!laggards || laggards.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Momentum Laggards
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No momentum laggards detected. All holdings are performing well!
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Momentum Laggards
          </h3>
          <button
            onClick={() => setShowModal(true)}
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            View All ({laggards.length})
          </button>
        </div>

        <div className="space-y-3">
          {topLaggards.map((laggard) => (
            <div
              key={laggard.ticker}
              className="flex items-center justify-between"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Link
                    to={
                      accountId
                        ? `/ticker/${laggard.ticker}?accountId=${accountId}`
                        : `/ticker/${laggard.ticker}`
                    }
                    className="font-semibold text-gray-900 dark:text-white hover:underline cursor-pointer"
                  >
                    {laggard.ticker}
                  </Link>
                  <span
                    className={`text-xs px-2 py-0.5 rounded ${
                      laggard.momentum_score <= -200
                        ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                        : laggard.momentum_score <= -100
                        ? "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200"
                        : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                    }`}
                  >
                    {getMomentumLabel(laggard.momentum_score)}
                  </span>
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  1 Yr CAGR: {formatPercent(laggard.cagr_1y)}
                </div>
              </div>

              <div className="text-right">
                <div
                  className={`font-semibold ${getMomentumColor(
                    laggard.momentum_score
                  )}`}
                >
                  {laggard.momentum_score.toFixed(1)}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {formatCurrency(laggard.current_value)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {showModal && (
        <LaggardAnalysisModal
          accountId={accountId}
          laggards={laggards}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
};

export default LaggardWidget;
